/**
 * Zustand store for conversation management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { WorkflowMetadata } from '@/types/workflow';
import { queryClient } from '@/lib/queryClient';

/**
 * Drop deleted conversations from the persisted react-query cache after a server-side
 * delete. Without this, PersistQueryClientProvider rehydrates the stale 'conversations'
 * snapshot on the next reload and the deleted project reappears until the background
 * refetch removes it again (the "delete → reappears on refresh → vanishes later" bug).
 *
 * Uses the bare ['conversations'] key (not conversationKeys from useConversations) to
 * avoid a circular import. The count queries hold a number, so the array guard skips
 * them; invalidateQueries then refetches lists + counts to reconcile the stale total.
 */
function dropFromConversationQueryCache(ids: string[]) {
  const idSet = new Set(ids);
  queryClient.setQueriesData<unknown>({ queryKey: ['conversations'] }, (old: any) => {
    if (!old || !Array.isArray(old.conversations)) return old;
    return { ...old, conversations: old.conversations.filter((c: { id: string }) => !idSet.has(c.id)) };
  });
  queryClient.invalidateQueries({ queryKey: ['conversations'] });
}

export interface Conversation {
  id: string;
  title: string;
  modelName: string;
  collectionType: 'VIDEO' | 'STYLE' | 'SLIDE'; // Must match Prisma CollectionType enum
  createdAt: Date;
  updatedAt: Date;
  lastActivity: Date;
  messageCount: number;
  thumbnailUrl?: string | null; // Project poster set via set_video_thumbnail (public CDN URL)
  slideThumbnailUrl?: string | null; // Preview URL for SLIDE conversations (from first slide's current version)
  slidePreviewUrls?: string[] | null; // Preview URLs for VIDEO conversations (first 4 slides for mosaic display)
  planJson?: Record<string, unknown> | null; // Video outline data (status, slides, etc.)
  scenes?: { built: number; total: number } | null; // VIDEO: the plan's scenes and how many are built
  lastFailure?: { code: string; at: string } | null; // VIDEO: the failure its last turn ended on
  metadata: {
    totalTokens?: number;
    avgResponseTime?: number;
    modelSwitches?: number;
    hasVideoProject?: boolean;
    hasStyleProject?: boolean;
    hasSlideProject?: boolean;
    projectId?: string;
    projectTitle?: string;
    projectThumbnail?: string;
    workingDirectory?: string;
    workflow?: WorkflowMetadata;
    appliedTemplateId?: string;
    appliedTemplateAt?: string;
  };
}

interface ConversationStore {
  // State
  conversations: Conversation[];
  activeConversationId: string | null;
  selectedConversationIds: string[];
  isMultiSelectMode: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setConversations: (conversations: Conversation[]) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  deleteConversation: (id: string) => void;
  deleteConversations: (ids: string[]) => void;
  setActiveConversation: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Multi-select actions
  toggleConversationSelection: (id: string) => void;
  selectAllConversations: () => void;
  clearSelection: () => void;
  setMultiSelectMode: (enabled: boolean) => void;

  // Video project actions
  updateConversationProject: (id: string, projectData: {
    hasVideoProject?: boolean;
    hasStyleProject?: boolean;
    hasSlideProject?: boolean;
    projectId?: string;
    workingDirectory?: string;
  }) => void;

  // Computed getters
  getActiveConversation: () => Conversation | null;
  getConversationById: (id: string) => Conversation | null;
}

// Helper to normalize date fields from API (strings) to Date objects
export const normalizeConversationDates = (conv: Conversation): Conversation => ({
  ...conv,
  createdAt: conv.createdAt instanceof Date ? conv.createdAt : new Date(conv.createdAt),
  updatedAt: conv.updatedAt instanceof Date ? conv.updatedAt : new Date(conv.updatedAt),
  lastActivity: conv.lastActivity instanceof Date ? conv.lastActivity : new Date(conv.lastActivity),
});

export const useConversationStore = create<ConversationStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      conversations: [],
      activeConversationId: null,
      selectedConversationIds: [],
      isMultiSelectMode: false,
      isLoading: false,
      error: null,

      // Actions
      setConversations: (conversations) => {
        // Normalize date fields from API strings to Date objects
        const normalized = conversations.map(normalizeConversationDates);
        set({ conversations: normalized }, false, 'setConversations');
      },

      addConversation: (conversation) => {
        // Normalize date fields from API strings to Date objects
        const normalized = normalizeConversationDates(conversation);
        set(
          (state) => ({
            conversations: [normalized, ...state.conversations],
          }),
          false,
          'addConversation'
        );
      },

      updateConversation: (id, updates) => {
        set(
          (state) => ({
            conversations: state.conversations.map((conv) =>
              conv.id === id ? { ...conv, ...updates } : conv
            ),
          }),
          false,
          'updateConversation'
        );
      },

      deleteConversation: (id) => {
        set(
          (state) => ({
            conversations: state.conversations.filter((conv) => conv.id !== id),
            activeConversationId:
              state.activeConversationId === id ? null : state.activeConversationId,
            selectedConversationIds: state.selectedConversationIds.filter(selectedId => selectedId !== id),
          }),
          false,
          'deleteConversation'
        );
        // Also evict from the persisted react-query cache so a reload can't resurrect it.
        dropFromConversationQueryCache([id]);
      },

      deleteConversations: (ids) => {
        set(
          (state) => ({
            conversations: state.conversations.filter((conv) => !ids.includes(conv.id)),
            activeConversationId:
              ids.includes(state.activeConversationId || '') ? null : state.activeConversationId,
            selectedConversationIds: [],
            isMultiSelectMode: false,
          }),
          false,
          'deleteConversations'
        );
        // Also evict from the persisted react-query cache so a reload can't resurrect them.
        dropFromConversationQueryCache(ids);
      },

      setActiveConversation: (id) => {
        set({ activeConversationId: id }, false, 'setActiveConversation');
      },

      setLoading: (isLoading) => {
        set({ isLoading }, false, 'setLoading');
      },

      setError: (error) => {
        set({ error }, false, 'setError');
      },

      clearError: () => {
        set({ error: null }, false, 'clearError');
      },

      // Multi-select actions
      toggleConversationSelection: (id) => {
        set(
          (state) => ({
            selectedConversationIds: state.selectedConversationIds.includes(id)
              ? state.selectedConversationIds.filter(selectedId => selectedId !== id)
              : [...state.selectedConversationIds, id],
          }),
          false,
          'toggleConversationSelection'
        );
      },

      selectAllConversations: () => {
        set(
          (state) => ({
            selectedConversationIds: state.conversations.map(conv => conv.id),
          }),
          false,
          'selectAllConversations'
        );
      },

      clearSelection: () => {
        set(
          { selectedConversationIds: [] },
          false,
          'clearSelection'
        );
      },

      setMultiSelectMode: (enabled) => {
        set(
          (state) => ({
            isMultiSelectMode: enabled,
            selectedConversationIds: enabled ? state.selectedConversationIds : [],
          }),
          false,
          'setMultiSelectMode'
        );
      },

      // Video project actions
      updateConversationProject: (id, projectData) => {
        set(
          (state) => ({
            conversations: state.conversations.map((conv) =>
              conv.id === id ? { ...conv, ...projectData } : conv
            ),
          }),
          false,
          'updateConversationProject'
        );
      },

      // Computed getters
      getActiveConversation: () => {
        const { conversations, activeConversationId } = get();
        return conversations.find((conv) => conv.id === activeConversationId) || null;
      },

      getConversationById: (id) => {
        const { conversations } = get();
        return conversations.find((conv) => conv.id === id) || null;
      },
    }),
    {
      name: 'conversation-store',
    }
  )
);